/*
 * Copyright 2020-2021 AskChristee, LLC. All rights reserved.
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Accordion, Tab, Tabs } from 'react-bootstrap';

import {
  FormInputCreditScore,
  FormInputCustEmail,
  FormInputDatePicker2,
  FormInputDollar,
  FormInputLoanType,
  FormInputLoanTypeHandle,
  FormInputPercent2,
  FormInputRadioButtons,
  FormInputStateAreaDropdowns,
  FormInputStateAreaDropdownsHandle,
  FormInputTerm,
  FormInputTermHandle,
  FormInputText,
  FormInputUseRatios,
  FormInputZip, FormLoader, FormWrapper,
  ValidationModal,
} from '../../../molecules';
import { hBUYERCHOICE } from '../../../../api';
import { Help } from '../../../molecules/QuickStart';

import {
  tipLim_piti,
  tipClose_date,
  tipCondo,
  tipCust_name,
  tipEmail,
  tipFiling_status,
  tipFtbuyer,
  tipInput_dol_cred,
  tipInput_ltv,
  tipInput_ma,
  tipInput_pmi_dollar,
  tipInput_uw_fee,
  tipInt_rate,
  tipLend_cred,
  tipM_debts,
  tipOrg_fee,
  tipProp_t,
  tipQ_income,
  tipS_cred,
  tipSp,
  tipT_cash,
  tipUse_ratios,
  tipVa_fee_ex,
  tipZip,
  UiSettingsProviderContext, PoweredBy,
} from '../../../atoms';
import { ChristeeConfigCtx } from '../../../context';

import { BuyerChoiceCashReport, BuyerChoiceReport } from '../../Reports';
import { fetchExemplar } from '../../../../api';
import { TBuyersChoiceResponse } from '../../../../types/api';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { toast } from 'react-toastify';
import { ButtonSendPdf } from '../../../molecules/ButtonSendPdf';
import ButtonDownloadPdf from '../../../molecules/ButtonDownloadPdf';
import HomeMainImage from "../../../../images/main.png"

const Exemplar = () => {
  const defaultValues = {
    account_type: '',
    condo: '',
    cust_email: '',
    cust_name: '',
    cust_phone: '',
    filing_status: 's',
    ftbuyer: 'n',
    input_dol_cred: '',
    input_ltv: '',
    input_ma: '',
    input_uw_fee: '',
    inputcred: '',
    int_rate: '',
    lend_cred: '',
    lim_piti: '',
    m_debts: '',
    org_fee: '',
    prop_t: '',
    q_income: '',
    s_cred: '',
    sp: '',
    t_cash: '',
  };
  const {
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
    trigger,
    setValue,
    formState: { errors, isValid, touchedFields, submitCount },
  } = useForm<any>({ mode: 'onTouched', reValidateMode: 'onBlur', defaultValues });
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { currentSettings } = useContext(UiSettingsProviderContext);

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  useEffect(() => {
    console.log('useEffect->scrollTop');
    scrollTop();
  }, []);

  const [dats, setDats] = useState<TBuyersChoiceResponse>();
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const ctx = useContext(ChristeeConfigCtx);
  const stateAreaRef = useRef<FormInputStateAreaDropdownsHandle>(null);
  const loanTypeRef = useRef<FormInputLoanTypeHandle>(null);
  const termRef = useRef<FormInputTermHandle>(null);

  const onSubmit = async (data: any) => {
    await trigger();
    scrollTop();
    setLoading(true);
    await new Promise((r) => setTimeout(r, 800));
    console.log('data', data);
    console.log('watch', watch);
    console.log('errors', errors);
    const doTypeArr = data.doType && data.doType.length > 0 ? data.doType.map((m: any) => m.value) : [];
    const filteredData = {
      ...data,
      state: data.state[0].code,
      county_select: data.county_select[0].code,
      doType: doTypeArr,
      term: Array.isArray(data.term) ? data.term[0].value : data.term ? data.term : '30',
    };

    try {
      let captchaToken;
      if (executeRecaptcha) {
        captchaToken = await executeRecaptcha();
      }
      const res = await fetchExemplar(
        {
          ...filteredData,
          company_name: currentSettings.companyId,
          cust_email: currentSettings.email,
          cust_name: currentSettings.name,
          cust_phone: currentSettings.phone,
        },
        ctx?.stage,
        ctx?.key,
        captchaToken
      );
      console.log('onSubmit->res', res);
      setDats(res);
      setLoaded(true);
      setLoading(false);
      console.log('dats after setDats', dats);
      scrollTop();
    } catch (error) {
      setLoading(false);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Something went wrong!');
      }
    }
  };

  const clearForm = (data: any) => {
    console.log('clearForm->data', data);
    console.log('clearForm->isValid', isValid);
    console.log('clearForm->errors', errors);
    console.log('clearForm->touchedFields', touchedFields);
    console.log('clearForm->submitCount', submitCount);
    console.log('clearForm->getValues->state', getValues('state'));
    stateAreaRef.current?.clear();
    loanTypeRef.current?.clear();
    termRef.current?.clear();
    reset(defaultValues);
    setDats({} as TBuyersChoiceResponse);
    setLoaded(false);
    setLoading(false);
    scrollTop();
  };

  const isSpendingCashAvailable = dats?.fha?.bCalc || dats?.conventional?.bCalc || dats?.va?.bCalc || dats?.usda?.bCalc;

  return (
    <FormWrapper>
      <div className="buyers-choice">
        <div className="container-fluid">
          <div className="row g-2">
            <div className="col-lg-3">
              <h3 className="h2">Mortgage Calculator</h3>
              <form className="christy" autoComplete="off">
                <fieldset>
                  <legend className="sr-only">Required Fields:</legend>
                  <FormInputStateAreaDropdowns setValue={setValue} stateWatch={watch('state')} areaWatch={watch('county_select')} control={control} errors={errors} ref={stateAreaRef} />
                  <FormInputZip label="Zip Code" tip={tipZip} error={errors.zip} name="zip" control={control} required={true} />
                  <FormInputDollar label="Sales Price" error={errors.sp} name="sp" control={control} required={true} tip={tipSp} />
                  <FormInputLoanType watch={watch('doType')} ref={loanTypeRef} control={control} errors={errors} required={true} />
                  <FormInputPercent2 label="Interest Rate" tip={tipInt_rate} error={errors.int_rate} name="int_rate" control={control} />
                  <FormInputTerm watch={watch('term')} ref={termRef} control={control} errors={errors} required={true} />
                  <FormInputDollar label="Annual Gross Income" tip={tipQ_income} error={errors.q_income} name="q_income" control={control} required={true} />
                  <FormInputDollar label="Monthly Debts Payment" tip={tipM_debts} error={errors.m_debts} name="m_debts" control={control} required={true} />
                  <FormInputCreditScore error={errors.inputcred} control={control} required={true} />
                  <FormInputRadioButtons
                    label="Married"
                    name="filing_status"
                    tip={tipFiling_status}
                    defaultValue="s"
                    control={control}
                    errors={errors}
                    required={true}
                    ids={['married', 'single']}
                    values={['m', 's']}
                  />
                  <FormInputRadioButtons
                    label="1st Time Buyer"
                    name="ftbuyer"
                    tip={tipFtbuyer}
                    defaultValue="n"
                    control={control}
                    errors={errors}
                    required={true}
                    ids={['first_time_yes', 'first_time_no']}
                  />
                </fieldset>
                <Accordion id="optional-inputs">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header data-cy="btn-optional">Optional Inputs</Accordion.Header>
                    <Accordion.Body>
                      <fieldset>
                        <FormInputUseRatios accountType={currentSettings.accountType} tip={tipUse_ratios} control={control} errors={errors} />
                        <FormInputDollar label="Cash" tip={tipT_cash} error={errors.t_cash} name="t_cash" control={control} />
                        <FormInputPercent2 label="Seller Credit" tip={tipS_cred} error={errors.s_cred} name="s_cred" control={control} />
                        <FormInputDollar label="Seller Credit" tip={tipInput_dol_cred} error={errors.input_dol_cred} name="input_dol_cred" control={control} />
                        <FormInputDollar label="Annual Property Taxes" tip={tipProp_t} error={errors.prop_t} name="prop_t" control={control} />
                        <FormInputDollar label="Limit Payment" tip={tipLim_piti} error={errors.lim_piti} name="lim_piti" control={control} />
                        <FormInputPercent2 label="Limit LTV" tip={tipInput_ltv} error={errors.input_ltv} name="input_ltv" control={control} />
                        <FormInputDollar label="Limit Loan Amount" tip={tipInput_ma} error={errors.input_ma} name="input_ma" control={control} />
                        <FormInputDollar label="Lender Fees" tip={tipInput_uw_fee} error={errors.input_uw_fee} name="input_uw_fee" control={control} />
                        <FormInputPercent2 label="Loan Origination" tip={tipOrg_fee} error={errors.org_fee} name="org_fee" control={control} max={2} />
                        <FormInputRadioButtons label="VA Exempt" name="va_fee_ex" tip={tipVa_fee_ex} defaultValue="n" control={control} errors={errors} ids={['va_fee_ex_yes', 'va_fee_ex_no']} />
                        <FormInputDollar label="Condo Fee" tip={tipCondo} error={errors.condo} name="condo" control={control} />
                        <FormInputRadioButtons label="PMI" name="input_pmi$" tip={tipInput_pmi_dollar} defaultValue="y" control={control} errors={errors} ids={['input_pmi_yes', 'input_pmi_no']} />
                        <FormInputPercent2 label="Lender Credit" tip={tipLend_cred} error={errors.lend_cred} name="lend_cred" control={control} />
                        <FormInputDatePicker2 label="Close Date" tip={tipClose_date} error={errors.close_date} name="close_date" control={control} />
                        <FormInputText label="Name" tip={tipCust_name} error={errors.cust_name} name="cust_name" control={control} />
                        <FormInputText label="Phone" tip={tipCust_name} error={errors.cust_phone} name="cust_phone" control={control} />
                        <FormInputCustEmail accountType={currentSettings.accountType} tip={tipEmail} error={errors.cust_email} control={control} />
                      </fieldset>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>

                <p className="mt-3">
                  <ValidationModal label="Submit" errors={errors} onClick={handleSubmit(onSubmit)} />
                  <button type="reset" className="btn btn-outline-secondary me-2" onClick={clearForm}>
                    Clear
                  </button>
                </p>
              </form>
            </div>

            <div className="col-md-9 col-xl-9">
              <div className="form-results">
                <div className="w-100 d-flex align-items-center justify-content-end mb-3">
                  <Help sFile={hBUYERCHOICE} />
                </div>
                {loading ? (
                  <FormLoader />
                ) : loaded && dats ? (
                  <Tabs defaultActiveKey={isSpendingCashAvailable ? "spending" : "cash"} className="mb-3">
                    <Tab eventKey="cash" title="Minimum Cash Results">
                      {loaded && dats ? (
                        <>
                          <br />
                          <BuyerChoiceReport {...dats! as any} />
                          <div className="d-flex mt-3">
                            <ButtonDownloadPdf data={dats} fileName="buyer-choice" />
                            {dats?.meta.email && dats?.meta.email !== 'nil' && <ButtonSendPdf dats={dats} />}
                          </div>
                        </>
                      ) : !loading ? (
                        <h3>Complete Form and Press Submit</h3>
                      ) : (
                        <></>
                      )}
                    </Tab>
                    {loaded && (dats?.fha.bCalc || dats?.conventional.bCalc || dats?.va.bCalc || dats?.usda.bCalc) ? (
                      <Tab eventKey="spending" title="Spending Cash">
                        {loaded && dats ? (
                          <>
                            <br />
                            <BuyerChoiceCashReport {...dats! as any} />
                            <ButtonDownloadPdf data={dats} fileName="buyer-choice" />
                          </>
                        ) : (
                          <h3>Complete Form and Press Submit - and Have lots of cash on hand!</h3>
                        )}
                      </Tab>
                    ) : (
                      <></>
                    )}
                  </Tabs>
                ) : (
                  <>
                    <h4 id="house-title" className="h4">
                      You’re seconds away from unlocking your Mortgage Calculator data.
                    </h4>
                    <img id="house-image" src={HomeMainImage} alt="home" />
                  </>
                )}
                <div className="w-100 mt-3">
                  <PoweredBy />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FormWrapper>
  );
};

export default Exemplar;
